import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import type { BlogPost } from "../types/blogPost";
import { normalizeBlogMedia } from "../utils/blogMediaUtils";

export interface BlogDetailResult {
  post: BlogPost | null;
  previous: BlogPost | null;
  next: BlogPost | null;
  otherPosts: BlogPost[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const OTHERS_LIMIT = 6;

export function useBlogDetail(id: string | undefined): BlogDetailResult {
  const [post, setPost] = useState<BlogPost | null>(null);
  const [previous, setPrevious] = useState<BlogPost | null>(null);
  const [next, setNext] = useState<BlogPost | null>(null);
  const [otherPosts, setOtherPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!id) {
      setPost(null);
      setPrevious(null);
      setNext(null);
      setOtherPosts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const { data: postData, error: postErr } = await supabase
      .from("blog_posts")
      .select("*")
      .eq("id", id)
      .single();

    if (postErr || !postData) {
      setError(postErr?.message ?? "Post not found");
      setPost(null);
      setPrevious(null);
      setNext(null);
      setOtherPosts([]);
      setLoading(false);
      return;
    }

    const current: BlogPost = {
      ...(postData as BlogPost),
      cover_image_path: (postData as BlogPost).cover_image_path ?? null,
      media: normalizeBlogMedia((postData as BlogPost).media),
    };
    setPost(current);

    const createdAt = current.created_at;

    const [prevRes, nextRes, othersRes] = await Promise.all([
      supabase
        .from("blog_posts")
        .select("*")
        .eq("is_published", true)
        .lt("created_at", createdAt)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from("blog_posts")
        .select("*")
        .eq("is_published", true)
        .gt("created_at", createdAt)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle(),
      supabase
        .from("blog_posts")
        .select("*")
        .eq("is_published", true)
        .neq("id", id)
        .order("created_at", { ascending: false })
        .limit(OTHERS_LIMIT),
    ]);

    setPrevious((prevRes.data as BlogPost) ?? null);
    setNext((nextRes.data as BlogPost) ?? null);
    setOtherPosts((othersRes.data as BlogPost[]) ?? []);

    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { post, previous, next, otherPosts, loading, error, refetch: fetch };
}
